import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, FormControlLabel, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Panel from '../reusable/Panel';
import useUserDetails from '../hooks/useUserDetails';
import { getCachedStates, getStates, State } from '../../services/eoaConfigServices';
import {
  DISTRICT_NAME_MAX_LENGTH,
  DistrictInformationForm,
  DsaSigned,
  districtNameError,
  filterStates,
} from './districtInformationForm';

interface DistrictInformationFieldsProps {
  value: DistrictInformationForm;
  onChange: (value: DistrictInformationForm) => void;
}

const placeholder = 'Your text here';

const DistrictInformationFields = ({ value, onChange }: DistrictInformationFieldsProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [states, setStates] = useState<State[]>(getCachedStates);

  useEffect(() => {
    const loadStates = async () => {
      if (!userDetails?.access_token) return;
      try {
        const response = await getStates(userDetails.eoaId ?? '', userDetails.access_token);
        setStates(response.data);
      } catch {
        setStates([]);
      }
    };

    loadStates();
  }, [userDetails]);

  const selectedState = useMemo(
    () => states.find((state) => state.state_id === value.stateId) ?? null,
    [states, value.stateId]
  );
  const districtNameHelperText = districtNameError(value.districtName);

  const updateField = <K extends keyof DistrictInformationForm>(field: K, fieldValue: DistrictInformationForm[K]) => {
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <>
      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">District information</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field eoa-configuration-field--state">
            <span className="eoa-configuration-field-label">State</span>
            <Autocomplete
              options={states}
              value={selectedState}
              getOptionLabel={(state) => state.name}
              isOptionEqualToValue={(option, optionValue) => option.state_id === optionValue.state_id}
              filterOptions={(options, { inputValue }) => filterStates(options, inputValue)}
              onChange={(_event, state) => updateField('stateId', state?.state_id ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for state"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">District name</span>
            <TextField
              fullWidth
              placeholder={placeholder}
              value={value.districtName}
              error={Boolean(districtNameHelperText)}
              helperText={districtNameHelperText}
              inputProps={{ maxLength: DISTRICT_NAME_MAX_LENGTH }}
              onChange={(event) => updateField('districtName', event.target.value)}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              Does this district appear with other names/values/aliases in the data? If so, what are they?
            </span>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={placeholder}
              value={value.districtAliases}
              onChange={(event) => updateField('districtAliases', event.target.value)}
            />
          </div>
        </div>
      </Panel>

      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">Student data</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">What SIS do you use (if applicable)?</span>
            <TextField
              fullWidth
              placeholder={placeholder}
              value={value.sis}
              onChange={(event) => updateField('sis', event.target.value)}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">What LMS do you use (if applicable)?</span>
            <TextField
              fullWidth
              placeholder={placeholder}
              value={value.lms}
              onChange={(event) => updateField('lms', event.target.value)}
            />
          </div>
        </div>
      </Panel>

      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">Data Sharing Agreement</h3>
        <div className="eoa-configuration-fields">
          <fieldset className="eoa-configuration-field eoa-configuration-dsa">
            <legend className="eoa-configuration-field-label">DSA signed?</legend>
            <RadioGroup
              name="dsa-signed"
              value={value.dsaSigned}
              onChange={(event) => updateField('dsaSigned', event.target.value as DsaSigned)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </fieldset>
        </div>
      </Panel>
    </>
  );
};

export default DistrictInformationFields;
